import { useEffect, useMemo, useState } from 'react';
import { ApiError, getErrorMessage } from '../api/client';
import { createCountSheet, getCountAssignees } from '../api/countSheets';
import { getIngredients } from '../api/inventory';
import type { CountAssignee, ManagerCountSheet } from '../types/countSheets';
import type { Ingredient } from '../types/inventory';
import { pickAllLabel, withAllVisible } from '../utils/countSheets';
import { randomUuid } from '../utils/randomUuid';
import { Answers, FooterReason, Question } from './capture';
import { Modal } from './Modal';
import { CheckRow } from './ui/CheckRow';
import { ProgressiveSection } from './ui/ProgressiveSection';

// F4 · armar una lista de conteo: UNA tienda, UNA persona y los insumos a contar. «Repetir» abre esta
// misma pantalla con lo de una lista anterior. La persona la verá en su Inicio y contará a ciegas.

export const MAX_COUNT_SHEET_LINES = 60;

export interface CountSheetDraft {
  storeId: string;
  assignedToUserId: string;
  ingredientIds: string[];
  note: string;
}

const ROLE_LABEL: Record<string, string> = { EMPLOYEE: 'Empleado', MANAGER: 'Gerente', OWNER: 'Dueño' };
const fold = (text: string) => text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

export function CountSheetCreateModal({ stores, initial, onClose, onCreated }: {
  stores: Array<{ id: string; name: string }>;
  initial?: Partial<CountSheetDraft>;
  onClose: () => void;
  onCreated: (sheet: ManagerCountSheet, replayed: boolean) => void;
}) {
  // «Repetir» con una tienda que ya no está disponible: no se cambia en silencio a otra; se pide elegir.
  const repeatStoreMissing = Boolean(initial?.storeId) && !stores.some((store) => store.id === initial?.storeId);
  const [storeId, setStoreId] = useState(initial?.storeId ? (repeatStoreMissing ? '' : initial.storeId) : stores.length === 1 ? stores[0]!.id : '');
  const [assignees, setAssignees] = useState<CountAssignee[]>();
  const [assignedToUserId, setAssignedToUserId] = useState(initial?.assignedToUserId ?? '');
  const [ingredients, setIngredients] = useState<Ingredient[]>();
  const [selected, setSelected] = useState<Set<string>>(() => new Set(initial?.ingredientIds ?? []));
  const [search, setSearch] = useState('');
  const [note, setNote] = useState(initial?.note ?? '');
  // Una llave por lista: un doble toque, o reintentar tras un resultado en duda, no crea dos listas.
  const [requestId, setRequestId] = useState(() => randomUuid());
  const [isBusy, setIsBusy] = useState(false);
  const [error, setError] = useState<string>();
  // P-079: «Cambiar» reabre la tienda o la persona con la elección puesta (nada se vacía hasta elegir otra).
  const [reopened, setReopened] = useState({ store: false, person: false });

  useEffect(() => {
    let alive = true;
    getIngredients()
      .then((result) => { if (alive) setIngredients(result.ingredients.filter((ingredient) => ingredient.isActive && (ingredient.approvalStatus ?? 'ACTIVE') === 'ACTIVE')); })
      .catch((loadError) => { if (alive) setError(getErrorMessage(loadError, 'No fue posible cargar los insumos.')); });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (!storeId) { setAssignees(undefined); return; }
    let alive = true;
    setAssignees(undefined);
    getCountAssignees(storeId)
      .then((result) => {
        if (!alive) return;
        setAssignees(result.users);
        // Si la persona elegida no tiene acceso a la tienda nueva, se quita en vez de fallar al guardar.
        setAssignedToUserId((current) => (result.users.some((user) => user.id === current) ? current : ''));
      })
      .catch((loadError) => { if (alive) setError(getErrorMessage(loadError, 'No fue posible cargar a quién asignar.')); });
    return () => { alive = false; };
  }, [storeId]);

  const visible = useMemo(() => {
    const query = fold(search.trim());
    return (ingredients ?? []).filter((ingredient) => !query || fold(ingredient.name).includes(query)).sort((a, b) => a.name.localeCompare(b.name, 'es'));
  }, [ingredients, search]);
  // Solo cuentan los insumos que siguen activos (una lista repetida puede traer uno ya desactivado).
  const chosen = (ingredients ?? []).filter((ingredient) => selected.has(ingredient.id));
  const missingFromRepeat = ingredients ? [...selected].filter((id) => !ingredients.some((ingredient) => ingredient.id === id)).length : 0;
  // Mientras carga la gente de la tienda elegida, la persona de antes no cuenta como elegida.
  const chosenPerson = assignees?.find((user) => user.id === assignedToUserId);
  const personChosen = Boolean(storeId && chosenPerson);
  const blocker = !storeId ? 'Elige la tienda.'
    : !assignees ? 'Cargando a quién asignar…'
      : !assignedToUserId || !assignees.some((user) => user.id === assignedToUserId) ? 'Elige a quién se le asigna.'
      : chosen.length === 0 ? 'Elige al menos un insumo.'
        : chosen.length > MAX_COUNT_SHEET_LINES ? `Una lista puede tener hasta ${MAX_COUNT_SHEET_LINES} insumos.`
          : null;
  const close = () => { if (!isBusy) onClose(); };
  const touch = () => { setRequestId(randomUuid()); setError(undefined); };
  const toggle = (id: string) => {
    setSelected((current) => { const next = new Set(current); if (next.has(id)) next.delete(id); else next.add(id); return next; });
    touch();
  };
  // «Elegir todos» elige los que se ven: sin búsqueda, todos; con búsqueda, solo los que coinciden.
  const notYetPicked = visible.filter((ingredient) => !selected.has(ingredient.id)).length;
  const pickVisible = () => {
    setSelected((current) => withAllVisible(current, visible.map((ingredient) => ingredient.id)));
    touch();
  };

  const submit = async () => {
    if (isBusy || blocker) return;
    setIsBusy(true);
    setError(undefined);
    try {
      const result = await createCountSheet({ requestId, storeId, assignedToUserId, ingredientIds: chosen.map((ingredient) => ingredient.id), ...(note.trim() ? { note: note.trim() } : {}) });
      onCreated(result.sheet, result.replayed);
    } catch (saveError) {
      if (saveError instanceof ApiError && saveError.status >= 400 && saveError.status < 500) {
        setError(getErrorMessage(saveError, 'No fue posible crear la lista.'));
        setRequestId(randomUuid());
      } else {
        setError('No sabemos si se creó: se perdió la conexión o el servidor no respondió. Toca «Asignar» otra vez; si ya se había creado, no se duplica.');
      }
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <Modal
      title="Nueva lista de conteo"
      description="La persona la verá en su Inicio y contará sin ver lo que dice el sistema."
      isBusy={isBusy}
      onClose={close}
      footer={<>
        <FooterReason>{blocker}</FooterReason>
        <button className="text-button" disabled={isBusy} onClick={close} type="button">Cancelar</button>
        <button className="button button--primary" disabled={isBusy || Boolean(blocker)} onClick={() => void submit()} type="button">{isBusy ? 'Asignando…' : `Asignar${chosen.length ? ` (${chosen.length})` : ''}`}</button>
      </>}
    >
      {error && <div className="alert alert--error" role="alert">{error}</div>}
      {repeatStoreMissing && <div className="alert alert--warning" role="status">La tienda de la lista anterior ya no está disponible. Elige otra.</div>}
      {/* P-079: Tienda y Persona como secciones progresivas (la pieza común): al elegir, se encogen y dejan la pantalla a
          los insumos (la hoja era larga y la nota del final quedaba bajo el teclado). «Cambiar» reabre con la elección
          puesta; la persona no cuenta como completa mientras carga la lista ni si perdió el acceso a la tienda nueva
          (entonces se vacía y la sección vuelve a abrirse, como siempre). Insumos y nota siguen abiertas: son el trabajo. */}
      {(stores.length > 1 || repeatStoreMissing) && (
        <ProgressiveSection busy={isBusy} complete={Boolean(storeId)} label="Tienda" onOpen={() => setReopened((current) => ({ ...current, store: true }))} open={reopened.store} summary={stores.find((store) => store.id === storeId)?.name ?? ''}>
          <Question>¿En qué tienda?</Question>
          <Answers>
            <label className="field"><span>Tienda</span>
              <select onChange={(event) => { setStoreId(event.target.value); touch(); if (event.target.value) setReopened((current) => ({ ...current, store: false })); }} value={storeId}>
                <option value="">Elige una tienda</option>
                {stores.map((store) => <option key={store.id} value={store.id}>{store.name}</option>)}
              </select>
            </label>
          </Answers>
        </ProgressiveSection>
      )}
      <ProgressiveSection busy={isBusy} complete={personChosen} label="Quién cuenta" onOpen={() => setReopened((current) => ({ ...current, person: true }))} open={reopened.person} summary={chosenPerson ? `${chosenPerson.name} · ${ROLE_LABEL[chosenPerson.role] ?? chosenPerson.role}` : ''}>
        <Question>¿Quién va a contar?</Question>
        <Answers>
          {!storeId ? <p className="wiz-hint">Primero elige la tienda.</p>
            : !assignees ? <div className="inline-loading"><span className="spinner" />Cargando…</div>
              : assignees.length === 0 ? <p className="wiz-hint">Nadie tiene acceso a esta tienda todavía. Asígnale la tienda a alguien en Usuarios.</p>
                : <label className="field"><span>Persona</span>
                  <select onChange={(event) => { setAssignedToUserId(event.target.value); touch(); if (event.target.value) setReopened((current) => ({ ...current, person: false })); }} value={assignedToUserId}>
                    <option value="">Elige a una persona</option>
                    {assignees.map((user) => <option key={user.id} value={user.id}>{user.name} · {ROLE_LABEL[user.role] ?? user.role}</option>)}
                  </select>
                </label>}
        </Answers>
      </ProgressiveSection>
      <Question>¿Qué insumos se cuentan?</Question>
      <Answers>
        {!ingredients ? <div className="inline-loading"><span className="spinner" />Cargando insumos…</div> : <>
          <label className="field"><span>Buscar</span><input onChange={(event) => setSearch(event.target.value)} placeholder="Ej. conos" type="search" value={search} /></label>
          {(notYetPicked > 0 || chosen.length > 0) && <div className="count-pick-tools">
            {notYetPicked > 0 && <button className="link-button" onClick={pickVisible} type="button">{pickAllLabel(visible.length, Boolean(search.trim()))}</button>}
            {chosen.length > 0 && <button className="link-button" onClick={() => { setSelected(new Set()); touch(); }} type="button">Quitar todos</button>}
          </div>}
          {/* P-076: renglones de selección de 44 px (antes, casillas de 16 px en renglones de 36). Mismo buscador,
              mismo «Elegir todos» y misma selección. */}
          <div aria-label="Insumos" className="count-pick-list n3-check-group__items" role="group">
            {visible.length === 0 ? <p className="wiz-hint">Ningún insumo coincide.</p>
              : visible.map((ingredient) => (
                <CheckRow checked={selected.has(ingredient.id)} hint={ingredient.baseUnit.symbol} key={ingredient.id} label={ingredient.name} onChange={() => toggle(ingredient.id)} />
              ))}
          </div>
          <p className="wiz-hint">
            {chosen.length ? `Elegidos: ${chosen.length}.` : 'Todavía no eliges ninguno.'}
            {missingFromRepeat > 0 && ` ${missingFromRepeat} de la lista anterior ya no están activos y se quitaron.`}
          </p>
        </>}
      </Answers>
      <Answers>
        <label className="field"><span>Nota (opcional)</span><input maxLength={200} onChange={(event) => { setNote(event.target.value); touch(); }} placeholder="Ej. Al cierre" type="text" value={note} /></label>
      </Answers>
    </Modal>
  );
}
